import { Dialog, DialogContent } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Alert from "src/components/atoms/alert/alert";
import ButtonAddNew from "src/components/atoms/button-add-new/button-add-new";
import PhoneNumberDisplay from "src/components/atoms/phone-number-display/phone-number-display";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import Banner from "src/components/molecules/banner/Banner";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import Card from "src/components/molecules/card/card";
import CheckBox from "src/components/molecules/check-box/check-box";
import LoadingPlaceHolder from "src/components/molecules/loading/loading-placeholder";
import RadioGroup from "src/components/molecules/radio-button-group/radio-button-groups";
import StatusBadge from "src/components/molecules/status-badge/status-badge";
import {
	IBusinessListModel,
	IBusinessModel,
	IBusinessNumberModel,
	INewApplicationModel,
	TOutboundCallFallBackEvent,
	TCallRoutingOption,
	IPhoneNumber,
	IBrandingAppModel,
	IContactCentreModel,
} from "src/types/models/apiModels";
import { TBrandingOptions } from "src/types/models/common";
import { ReactComponent as IncomingCallIcon } from "../../../assets/svg/incomingCallIcon.svg";
import { ReactComponent as OutgoingCallIcon } from "../../../assets/svg/outgoingCallIcon.svg";
import ActionPanel from "../action-panel/action-panel";
import { useAuth } from "../auth/auth-context";
import BrandingConfiguration from "../branding-configuration/branding-configuration";
import BusinessList from "../business-list/business-list";
import { BusinessNumbersConfigureViewModel } from "../business-numbers-configure/business-numbers-configure.view-model";
import BusinessNumbersList from "../business-numbers-list/business-numbers-list";
import ContactCentreList from "../contact-centre-list/contact-centre-list";
import MobileAppList from "../mobile-app-list/mobile-app-list";
import { NewApplicationViewModel } from "./application-add-new-view-model";
import { ApplicationAddNewProps } from "./application-add-new.props";
import { defaultScreenSetting } from "src/models/default-screen-settings";
import { CopyIcon } from "lucide-react";
import { ClipboardService } from "src/helpers/clipboard-service";
import RadioGroupSquare from "src/components/molecules/radio-button-group/radio-button-groups-square";
import PhoneNumberField from "src/components/molecules/phoneNumberField/PhoneNumberField";

const ApplicationAddNew: React.FC<ApplicationAddNewProps> = ({
	onSubmitClick,
	onCancelClick,
}) => {
	const defaultCC: IContactCentreModel = {
		contactCentreId: "NA",
		contactCentreCode: "NA",
		productName: "NA",
		name: "Not Applicable",
		option: "ExternalContactCentre",
		whitelistedUrls: [],
	};

	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const PLEASESELECT = "PLEASE SELECT";
	const NA = "NA";
	const { user } = useAuth(); // Extract user from useAuth hook
	const [loading, setLoading] = useState(false);
	const [applicationFriendlyName, setApplicationFriendlyName] = useState("");
	const [description, setDescription] = useState("");
	const [hasMoreThanOneApp, setHasMoreThanOneApp] = useState(true);
	const [business, setBusiness] = useState<IBusinessModel | undefined>(
		undefined
	);
	const [businessListItem, setBusinessListItem] = useState<
		IBusinessListModel | undefined
	>(undefined);
	const [contactCentre, setContactCentre] = useState<
		IContactCentreModel | undefined
	>(undefined);
	const [contactCentreId, setContactCentreId] = useState<string>("NA");
	const [brandingOption, setBrandingOption] = useState<TBrandingOptions>(
		"UnmanagedCallBranding"
	);
	const [routeOutgoingCallsTo, setRouteOutgoingCallsTo] =
		useState<TCallRoutingOption>("WEBRTC");
	const [routeIncomingCallsTo, setRouteIncomingCallsTo] =
		useState<TCallRoutingOption>("SIP");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [inboundPhoneNumber, setInboundPhoneNumber] = useState<IPhoneNumber>({
		countryCode: "",
		number: "",
	});
	const [inboundSipDomain, setInboundSipDomain] = useState("");
	const [inboundCallAnnouncementText, setInboundCallAnnouncementText] =
		useState("Please wait while we connect you to the agent");
	const [outboundCallAnnouncementText, setOutboundCallAnnouncementText] =
		useState("Please wait while we connect you to the user");
	const [enableInboundCalling, setEnableInboundCalling] = useState(false);
	const [enableOutboundCalling, setEnableOutboundCalling] = useState(false);
	const [contactCentreList, setContactCentreList] = useState<
		IContactCentreModel[]
	>([defaultCC]);
	const [approvedBusinessNumbers, setApprovedBusinessNumbers] = useState<
		IBusinessNumberModel[]
	>([]);
	const [selectedMobileApp, setSelectedMobileApp] = useState<
		IBrandingAppModel | undefined
	>(undefined);

	const [selectBusinessDialogOpen, setSelectBusinessDialogOpen] =
		useState<boolean>(false);
	const [selectCCDialogOpen, setSelectCCDialogOpen] =
		useState<boolean>(false);
	const [selectAppDialogOpen, setSelectAppDialogOpen] =
		useState<boolean>(false);
	const [webRTCCallTimeoutInSeconds, setWebRTCCallTimeoutInSeconds] =
		useState(10);
	const [numberDialogOpen, setNumberDialogOpen] = useState<boolean>(false);
	const [
		showContactCenterMandatoryOption,
		setShowContactCenterMandatoryOption,
	] = useState(false);
	const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
	const [showHelp, setShowHelp] = useState(true);
	const [error, setError] = useState<string>("");
	const [businessNumberFilteredValue, setBusinessNumberFilteredValue] =
		useState<string>("");
	const viewModel = useRef<NewApplicationViewModel | null>(null);
	const businessNumbersConfigureViewModel =
		useRef<BusinessNumbersConfigureViewModel | null>(null);
	const [fallbackEvents, setFallbackEvents] = useState<
		TOutboundCallFallBackEvent[]
	>([]);
	const [ringerTimeout, setRingerTimeout] = useState(10);

	useEffect(() => {
		const fetchData = async () => {
			setLoading((prevValue) => {
				if (!prevValue) {
					return true;
				}
				return prevValue;
			});
			try {
				if (user && user.accessToken) {
					viewModel.current = new NewApplicationViewModel(
						user.accessToken
					);
					const businessList =
						await viewModel.current?.fetchBusinessList();

					const approvedBusinesses = businessList.filter(
						(p) => p.status === "Active" || p.status === "Approved"
					);

					if (approvedBusinesses && approvedBusinesses.length >= 1) {
						setBusinessListItem(() => approvedBusinesses[0]);
					}

					const appList =
						await viewModel.current?.fetchMobileAppList();

					if (appList && appList.length === 1) {
						setHasMoreThanOneApp(false);
						setSelectedMobileApp({
							option: "ExternalApp",
							brandingAppCode: appList[0].code,
							brandingAppId: appList[0].id,
							brandingAppName: appList[0].name,
						});
					}

					const ccList =
						await viewModel.current?.fetchContactCentreList();

					if (ccList) {
						ccList.push(defaultCC);
						setContactCentreList(() => ccList);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading((prevValue) => {
					if (prevValue) {
						return false;
					}
					return prevValue;
				});
			}
		};
		fetchData();
	}, [user]);

	useEffect(() => {
		const fetchBusinessNumbers = async () => {
			if (business) {
				try {
					if (user && user.accessToken) {
						businessNumbersConfigureViewModel.current =
							new BusinessNumbersConfigureViewModel(
								user.accessToken
							);

						const response =
							await businessNumbersConfigureViewModel.current?.fetchListAsync(
								business.businessId
							);

						const aNumbers: IBusinessNumberModel[] = response
							.filter((number) => number.status === "Approved") // Filter only approved numbers
							.map((number) => {
								// Ensure managedNumberSettings is initialized
								number.selected = false;
								if (!number.managedNumberSettings) {
									number.managedNumberSettings = {
										cPaaSProvider: "Vonage",
										screenSettings: defaultScreenSetting,
									};
								}
								return number; // Return the updated number
							});

						setApprovedBusinessNumbers(aNumbers); // Update the state
						setEnableInboundCalling(false);
						setEnableOutboundCalling(false);
					}
				} catch (error) {
					console.error("Error fetching data:", error);
				} finally {
					setLoading((prevValue) => {
						if (prevValue) {
							return false;
						}
						return prevValue;
					});
				}
			}
		};
		fetchBusinessNumbers();
	}, [business]);

	useEffect(() => {
		const bus = businessListItem
			? {
					businessId: businessListItem.businessId,
					businessCode: businessListItem.businessCode,
					name: businessListItem.name,
					logo: businessListItem.logo,
				}
			: undefined;
		setBusiness(() => bus);
	}, [businessListItem]);

	useEffect(() => {
		const fetchData = async () => {
			const cc = contactCentreList.find(
				(p) => (p.contactCentreId = contactCentreId)
			);
			setContactCentre(() => cc);
		};
		fetchData();
	}, [contactCentreId]);

	useEffect(() => {
		setInboundSipDomain("");
		setPhoneNumber("");
	}, [routeIncomingCallsTo]);

	useEffect(() => {
		setInboundSipDomain("");
	}, [enableInboundCalling]);

	useEffect(() => {
		setInboundSipDomain("");
		setEnableInboundCalling(false);
	}, [brandingOption]);

	const toggleAccordion = (index: number) => {
		setActiveIndex(activeIndex === index ? null : index);
	};

	const handleSubmitApplication = async () => {
		setLoading(true);
		try {
			if (user && user.accessToken) {
				viewModel.current = new NewApplicationViewModel(
					user.accessToken
				);

				if (
					brandingOption === "UnmanagedCallBranding" &&
					contactCentre?.contactCentreId == "NA"
				) {
					setShowContactCenterMandatoryOption(true);
					return;
				}

				if (selectedMobileApp && business && brandingOption) {
					const request: INewApplicationModel = {
						name: applicationFriendlyName,
						description: description,
						countryCode: "AU",
						brandingApp: {
							option: selectedMobileApp.option,
							brandingAppCode:
								selectedMobileApp.option === "ExternalApp"
									? selectedMobileApp?.brandingAppId
									: undefined,
						},
						contactCentre:
							contactCentre?.contactCentreId !== "NA"
								? {
										option: "ExternalContactCentre",
										contactCentreId:
											contactCentre?.contactCentreId ===
												"NA" ||
											contactCentre?.contactCentreId ===
												undefined
												? ""
												: contactCentre?.contactCentreId,
									}
								: {
										option: "None",
									},
						businessId: business?.businessId,
						brandingOption: brandingOption,
						managedApplicationSettings:
							brandingOption === "CPaaSManagedCallBranding"
								? {
										cPaaSProvider: "Vonage",
										outBoundCall: {
											routingOption: routeOutgoingCallsTo,
											timeout: ringerTimeout,
											announcementText:
												outboundCallAnnouncementText,
											fallBackEvents: fallbackEvents,
											alwaysUseDefaultIntent:
												contactCentre?.contactCentreId ===
												"NA"
													? true
													: false,
										},
										inboundCall: enableInboundCalling
											? {
													routingOption:
														routeIncomingCallsTo,
													announcementText:
														inboundCallAnnouncementText,
													sipDomain: inboundSipDomain,
													phoneNumber: phoneNumber,
												}
											: undefined,
									}
								: undefined,
						businessNumbers: approvedBusinessNumbers
							.filter((p) => p.selected) // only send the selected numbers in the application request.
							.map((selectedNumber) => ({
								businessNumber: selectedNumber.businessNumber,
								branding: {
									brandingName:
										selectedNumber.branding.brandingName,
									defaultIntent:
										selectedNumber.branding.defaultIntent,
									defaultTTLInMinutes:
										selectedNumber.branding
											.defaultTTLInMinutes,
									imageFile:
										selectedNumber.branding.imageFile,
									setupPushContent:
										selectedNumber.branding
											.setupPushContent,
									cleanupPushContent:
										selectedNumber.branding
											.cleanupPushContent,
								},
								managedNumberSettings:
									brandingOption ===
									"CPaaSManagedCallBranding"
										? {
												cPaaSProvider: "Vonage",
												screenSettings:
													selectedNumber
														.managedNumberSettings
														.screenSettings,
											}
										: undefined,
							})),
						scProvidedNumbers: true,
					};
					// console.log(JSON.stringify(request));

					const saveResult =
						await viewModel.current?.submitApplication(request);
					// console.log("response", saveResult);

					if (saveResult?.result === true) {
						setLoading(false);
						onSubmitClick(request);
					} else {
						setLoading(false);
						setError(() => saveResult?.error ?? "");
						toggleError(true);
					}
				}
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setLoading((prevValue) => {
				if (prevValue) {
					return false;
				}
				return prevValue;
			});
		}
	};

	const toggleError = (value: boolean) => {
		setErrorDialogOpen((prevValue) => {
			if (prevValue !== value) {
				return value;
			}
			return prevValue;
		});
	};

	const toggleHelp = () => {
		setShowHelp((prev) => !prev);
	};

	const handleCancel = () => {
		if (onCancelClick) {
			onCancelClick();
		}
	};

	const toggleBusinessDialog = () => {
		setSelectBusinessDialogOpen((prevValue) => !prevValue);
	};

	const toggleCCDialog = () => {
		setSelectCCDialogOpen((prevValue) => !prevValue);
	};

	const toggleAppDialog = () => {
		setSelectAppDialogOpen((prevValue) => !prevValue);
	};

	const toggleNumberDialog = () => {
		setNumberDialogOpen((prevValue) => !prevValue);
	};

	const toggleShowContactCenterMandatoryOption = () => {
		setShowContactCenterMandatoryOption((prevValue) => !prevValue);
	};

	if (loading) {
		return (
			<LoadingPlaceHolder message="We're setting things up for you. Just a moment!" />
		);
	}

	function handleOnAddBusiness(): void {
		throw new Error("Function not implemented.");
	}

	function handleOnSelectBusiness(business: IBusinessListModel): void {
		setBusinessListItem(business);
		toggleBusinessDialog();
	}

	function showBusinessList(): void {
		toggleBusinessDialog();
	}

	function handleOnSelectCC(contactCentre: IContactCentreModel): void {
		setContactCentre(contactCentre);
		toggleCCDialog();
	}

	function showCCList(): void {
		toggleCCDialog();
	}

	function handleOnSelectApp(selectedMobileApp: IBrandingAppModel): void {
		setSelectedMobileApp(selectedMobileApp);
		toggleAppDialog();
	}

	function showAppList(): void {
		toggleAppDialog();
	}

	const handleOutgoingCallRoutingChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "WEBRTC") {
			setRouteOutgoingCallsTo("WEBRTC");
		} else if (selectedOption.value === "PSTN") {
			setRouteOutgoingCallsTo("PSTN");
		}
	};

	const handleIncomingCallRoutingChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "SIP") {
			setRouteIncomingCallsTo("SIP");
		} else if (selectedOption.value === "PSTN") {
			setRouteIncomingCallsTo("PSTN");
		}
	};

	const handleRadioChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "unmanaged") {
			setBrandingOption("UnmanagedCallBranding");
		} else if (selectedOption.value === "managed") {
			setBrandingOption("CPaaSManagedCallBranding");
		}
	};

	const handleCheckboxChange = (
		eventName: TOutboundCallFallBackEvent,
		isChecked: boolean
	) => {
		setFallbackEvents((prev) => {
			// If unchecked, remove the event
			if (!isChecked) {
				return prev.filter((event) => event !== eventName); // Always return an array
			}
			// If checked, add the event if not already present
			return prev.includes(eventName) ? prev : [...prev, eventName];
		});
	};

	return (
		<>
			<div className="flex flex-col items-start justify-start gap-4 w-full">
				<div className="flex gap-2 w-full px-4">
					<div className="flex flex-col items-start justify-start gap-2 w-full">
						<Card
							heading={"Business"}
							code={""}
							image={business?.logo?.signedUrl}
							name={business ? business.name : PLEASESELECT}
							status={business?.status}
							onEditClick={showBusinessList}
						/>
					</div>
					<div className="flex flex-col items-start justify-start gap-2 w-full">
						<Card
							heading={"Mobile App"}
							code={
								selectedMobileApp
									? (selectedMobileApp.brandingAppCode ??
										PLEASESELECT)
									: PLEASESELECT
							}
							name={
								selectedMobileApp
									? (selectedMobileApp.brandingAppName ??
										PLEASESELECT)
									: PLEASESELECT
							}
							onEditClick={
								hasMoreThanOneApp ? showAppList : undefined
							}
						/>
					</div>
					<div className="flex flex-col items-start justify-start gap-2 w-full">
						<Card
							heading={"Contact Center"}
							code={
								contactCentre?.contactCentreCode === NA
									? PLEASESELECT
									: contactCentre?.contactCentreCode ||
										PLEASESELECT // Provide a default fallback
							}
							name={
								contactCentre?.contactCentreCode === NA
									? PLEASESELECT
									: contactCentre?.name || PLEASESELECT // Provide a default fallback
							}
							onEditClick={showCCList}
						/>
					</div>
				</div>
				<div className="w-full flex flex-col items-center justify-center gap-4  px-4">
					<InputBox
						label={"Name"}
						type={"text"}
						required
						value={applicationFriendlyName}
						onChange={function (value: string): void {
							setApplicationFriendlyName(value);
						}}
					></InputBox>
					<textarea
						className="bg-slate-50 font-Poppins font-medium w-full border-b-2 border-gray-400"
						rows={4} // Number of visible rows
						maxLength={255}
						placeholder="Enter description here..."
						value={description} // Replace 'description' with your state variable
						onChange={(e) => setDescription(e.target.value)} // Replace 'setDescription' with your setter function
					></textarea>
				</div>
				<div className="p-4 flex flex-col items-start justify-center gap-6 w-full">
					<div className="flex items-start justify-start gap-2 w-full">
						<span className="font-semibold text-lg uppercase">
							Application Type
						</span>
						{/* <ButtonWithIcon
							onClick={function (): void {
								toggleShowApplicationTypeHelp();
							}}
							icon={<HelpIcon className="w-6 h-6"></HelpIcon>}
						></ButtonWithIcon> */}
					</div>
					<div className="flex flex-col items-start justify-start gap-4 w-full">
						<RadioGroupSquare
							options={[
								{
									label: "Unmanaged Calls",
									value: "unmanaged",
								},
								{
									label: "Managed Calls",
									value: "managed",
								},
							]}
							defaultSelected="unmanaged"
							onChange={handleRadioChange}
						/>
						<div className="w-full">
							{brandingOption === "CPaaSManagedCallBranding" ? (
								//todo: add read more link
								<Alert
									text={
										"The Managed Calls option provides a complete calling solution by integrating SIP-based call routing with branded call experiences. This approach is ideal for businesses that require both branding and call handling capabilities."
									}
									type={"info"}
									isHTML={true}
								></Alert>
							) : (
								//todo: add read more link
								<Alert
									text={
										"The Unmanaged Calls option is designed for businesses that only need branding support without relying on us to handle the calling process. In this model, businesses are responsible for call initiation and routing through their existing contact center setup."
									}
									isHTML={true}
									type={"info"}
								></Alert>
							)}
						</div>
					</div>
					{brandingOption === "CPaaSManagedCallBranding" && (
						<div className="flex items-start justify-center gap-4 h-full w-full">
							<div className="flex w-full h-full">
								<BoxWithIcon
									icon={
										<OutgoingCallIcon className="w-4 h-4"></OutgoingCallIcon>
									}
									title={"Outbound Calling"}
								>
									<div className="flex flex-col items-start justify-center gap-6 w-full h-full p-2">
										<CheckBox
											title="Enabled"
											checked={enableOutboundCalling}
											onChange={function (
												value: boolean
											): void {
												setEnableOutboundCalling(value);
											}}
										></CheckBox>

										{enableOutboundCalling && (
											<>
												<InputBox
													label={"Call announcement"}
													type={"text"}
													value={
														outboundCallAnnouncementText
													}
													onChange={function (
														value: string
													): void {
														setOutboundCallAnnouncementText(
															value
														);
													}}
												></InputBox>

												<span className="font-semibold text-lg text-sky-600">
													Route outgoing calls to
												</span>
												<RadioGroup
													options={[
														{
															label: `${selectedMobileApp?.brandingAppName ?? "Mobile App"} `,
															value: "WEBRTC",
														},
														{
															label: "Mobile Network / PSTN",
															value: "PSTN",
														},
													]}
													defaultSelected={
														routeOutgoingCallsTo
													}
													onChange={
														handleOutgoingCallRoutingChange
													}
												/>

												{routeOutgoingCallsTo ===
													"WEBRTC" && (
													<div className="flex flex-col items-start justify-center gap-4 w-full">
														<InputBox
															label={
																"Ringer timeout in seconds"
															}
															type={"number"}
															value={`${ringerTimeout}`}
															maxLength={3} // Ensuring max length for 120
															onChange={(
																value: string
															): void => {
																const parsedValue =
																	Number.parseInt(
																		value
																	);

																if (
																	isNaN(
																		parsedValue
																	) ||
																	parsedValue <
																		0
																) {
																	setRingerTimeout(
																		10
																	); // Default fallback value
																} else if (
																	parsedValue >
																	120
																) {
																	setRingerTimeout(
																		120
																	); // Max limit
																} else {
																	setRingerTimeout(
																		parsedValue
																	);
																}
															}}
														></InputBox>
														<span className="font-semibold text-lg text-sky-600">
															Fallback to PSTN
															call when the call
															is
														</span>
														<div className="flex items-center justify-center py-2 space-x-2">
															<CheckBox
																title="Unanswered"
																onChange={(
																	value
																) =>
																	handleCheckboxChange(
																		"timeout",
																		value
																	)
																}
															></CheckBox>

															<CheckBox
																title="Failed"
																onChange={(
																	value
																) =>
																	handleCheckboxChange(
																		"failed",
																		value
																	)
																}
															></CheckBox>
														</div>
														{/* <div className="w-full">
													<Alert
														text={
															"Outgoing calls are charged @ A$0.016/min"
														}
														type={"info"}
													></Alert>
												</div> */}
													</div>
												)}
											</>
										)}
									</div>
								</BoxWithIcon>
							</div>
							<div className="flex w-full h-full">
								<BoxWithIcon
									icon={
										<IncomingCallIcon className="w-4 h-4"></IncomingCallIcon>
									}
									title={"Inbound Calling"}
								>
									<div className="flex flex-col items-start justify-center gap-6 w-full h-full p-2">
										<CheckBox
											title="Enabled"
											checked={enableInboundCalling}
											onChange={function (
												value: boolean
											): void {
												setEnableInboundCalling(value);
											}}
										></CheckBox>

										{enableInboundCalling && (
											<>
												<InputBox
													label={"Call announcement"}
													type={"text"}
													value={
														inboundCallAnnouncementText
													}
													onChange={function (
														value: string
													): void {
														setInboundCallAnnouncementText(
															value
														);
													}}
												></InputBox>
												<span className="font-semibold text-lg text-sky-600">
													Route incoming calls to
												</span>
												<RadioGroup
													options={[
														{
															label: `SIP`,
															value: "SIP",
														},
														{
															label: "Phone number",
															value: "PSTN",
														},
													]}
													defaultSelected={
														routeIncomingCallsTo
													}
													onChange={
														handleIncomingCallRoutingChange
													}
												/>
												{routeIncomingCallsTo ===
												"SIP" ? (
													<InputBox
														label={
															"Inbound SIP Domain with port"
														}
														type={"text"}
														required
														value={inboundSipDomain}
														onChange={function (
															value: string
														): void {
															setInboundSipDomain(
																value
															);
														}}
														helpText="e.g. (securedcalls.3cx.com.au:5060)"
													></InputBox>
												) : (
													<>
														<PhoneNumberField
															phoneNumber={
																inboundPhoneNumber
															}
															value={phoneNumber}
															onChange={(
																phoneNumber,
																value
															) =>
																setPhoneNumber(
																	value
																)
															}
															showCountry={false}
														></PhoneNumberField>
													</>
												)}
												{/* <CheckBox
													title="Web widget"
													checked={
														enableInboundCalling
													}
													onChange={function (
														value: boolean
													): void {
														setEnableInboundCalling(
															value
														);
													}}
												></CheckBox> */}

												{/* <div className="w-full">
													<Alert
														text={
															"Inbound calls are charged @ A$0.016/min"
														}
														type={"info"}
													></Alert>
												</div> */}
											</>
										)}
									</div>
								</BoxWithIcon>
							</div>
						</div>
					)}
				</div>
				<div className="p-4 flex flex-col items-start justify-center gap-6 w-full">
					<div className="flex flex-col items-start justify-center gap-4 w-full">
						<span className="font-semibold text-lg uppercase">
							Branding Numbers ({approvedBusinessNumbers.length})
						</span>
						{business && approvedBusinessNumbers.length <= 0 && (
							<div className="w-full">
								{brandingOption ===
								"CPaaSManagedCallBranding" ? (
									//todo: add read more link
									<Alert
										text={`You currently do not have approved numbers for this business. If you require number provisioning, please proceed to submit this form. Our backend team will assist with provisioning the numbers. Once the numbers are provisioned, you can return here to apply
									branding to them.`}
										type={"error"}
									></Alert>
								) : (
									//todo: add read more link
									<Alert
										text={`You currently do not have approved numbers for this business. Please proceed to add new numbers to the business. Once submitted, our backend team will review and approve the numbers. After approval, you can return here to select the numbers and apply branding to them.`}
										type={"error"}
									></Alert>
								)}
							</div>
						)}
						<div className="flex items-center justify-start w-full">
							<ButtonAddNew
								text="Select Numbers"
								onClick={toggleNumberDialog}
								disabled={approvedBusinessNumbers.length === 0}
							></ButtonAddNew>
						</div>

						{approvedBusinessNumbers.filter((p) => p.selected)
							.length > 0 && (
							<>
								<InputBox
									value={businessNumberFilteredValue}
									label={"Filter"}
									type={"text"}
									onChange={function (value: string): void {
										setBusinessNumberFilteredValue(value);
									}}
								></InputBox>

								{/* todo: consider to move this to its own component later. */}
								{approvedBusinessNumbers.map((p) => p.selected)
									.length > 0 && (
									<>
										{/* <div className="flex items-center justify-start w-full">
										<CheckBox
											title="Show Modified only"
											onChange={(
												value: boolean
											): void => {
												console.log(value);
											}}
											checked={true}
										></CheckBox>
									</div> */}
										<div id="accordion" className="w-full">
											<div className="flex items-center justify-start w-full p-2">
												<span className="font-semibold text-sm">
													Click on individual numbers
													to configure branding.
												</span>
											</div>
											{approvedBusinessNumbers
												.filter(
													(p) => p.selected === true
												)
												.map((item, index) => (
													<div
														key={index}
														className="pb-2"
													>
														<h2
															className={`${item.hasUnsavedChanges ? "bg-gradient-to-r from-sky-400 to-sky-200 hover:bg-sky-200" : ""} flex items-center justify-between w-full py-3 px-4 pe-6 font-medium text-gray-800 bg-slate-200 rounded border-gray-200 hover:bg-gray-100`}
														>
															{/* Accordion toggle area */}
															<button
																type="button"
																className="flex items-center justify-start gap-2 w-full"
																onClick={() =>
																	toggleAccordion(
																		index
																	)
																}
																aria-expanded={
																	activeIndex ===
																	index
																}
																aria-controls={`accordion-body-${index}`}
															>
																<img
																	key={
																		item.businessNumber
																	}
																	className="w-12 h-12 rounded-full flex-shrink-0 drop-shadow-md"
																	src={
																		item
																			.branding
																			?.imageFile
																			?.signedUrl ||
																		business
																			?.logo
																			?.signedUrl
																	}
																	alt=""
																/>
																<span
																	className={`${item.hasUnsavedChanges ? "text-white" : "text-gray-900"} font-semibold text-lg`}
																>
																	<PhoneNumberDisplay
																		number={
																			item.businessNumber
																		}
																		hasPlus={
																			false
																		}
																	></PhoneNumberDisplay>
																</span>
																<button
																	className="pr-2 cursor-copy"
																	onClick={(
																		e
																	) => {
																		e.stopPropagation();

																		ClipboardService.copyToClipboard(
																			"+" +
																				item.businessNumber
																		);
																	}}
																>
																	<CopyIcon className="w-4 h-4"></CopyIcon>
																</button>
															</button>

															{/* Action buttons */}
															<div className="flex items-center justify-end gap-2 w-fit">
																{/* Remove button */}
																{/* {item.hasUnsavedChanges && (
																<div className="rounded-2xl bg-blue-500 w-full">
																	<button
																		className={`py-1 px-4 rounded-full text-sm font-semibold text-white text-nowrap`}
																	>
																		{
																			"Discard changes"
																		}
																	</button>
																</div>
															)} */}
																<StatusBadge
																	status={
																		item.status
																	}
																></StatusBadge>
																{item.hasUnsavedChanges && (
																	<div className="rounded-2xl bg-orange-500 w-full">
																		<span
																			className={`py-1 px-4 rounded-full text-sm font-semibold text-white text-nowrap`}
																		>
																			{
																				"Modified"
																			}
																		</span>
																	</div>
																)}

																<div className="rounded-2xl bg-blue-500 w-full">
																	<span
																		className={`py-1 px-4 rounded-full text-sm font-semibold text-white text-nowrap`}
																	>
																		{
																			"Pending"
																		}
																	</span>
																</div>
															</div>
														</h2>

														{/* Accordion content */}
														<div
															id={`accordion-body-${index}`}
															className={`overflow-hidden transition-all duration-300 ${
																activeIndex ===
																index
																	? "h-full"
																	: "max-h-0"
															}`}
														>
															<div className="pt-2 text-gray-500">
																{/* {item.content} */}
																<>
																	{business && (
																		<BrandingConfiguration
																			business={
																				business
																			}
																			number={
																				item
																			}
																			scProvidedNumber={
																				true
																			}
																			onChange={(
																				value
																			) => {
																				setApprovedBusinessNumbers(
																					(
																						prevNumbers
																					) =>
																						prevNumbers.map(
																							(
																								num
																							) =>
																								num.businessNumber ===
																								value.businessNumber
																									? {
																											...num,
																											...value,
																										}
																									: num
																						)
																				);
																			}}
																			showInAppModel={
																				brandingOption ===
																				"CPaaSManagedCallBranding"
																			}
																			showInAppBranding={
																				routeOutgoingCallsTo ===
																				"WEBRTC"
																			}
																			inboundCallSettings={
																				enableInboundCalling
																					? {
																							announcementText:
																								inboundCallAnnouncementText,
																							sipEndpointUrl:
																								inboundSipDomain,
																						}
																					: undefined
																			}
																			appName={
																				selectedMobileApp?.brandingAppName
																			}
																		/>
																	)}
																</>
															</div>
														</div>
													</div>
												))}
										</div>
									</>
								)}
							</>
						)}
					</div>

					<ActionPanel
						primaryButtonLabel={"Submit"}
						primaryButtonEnabled={
							enableInboundCalling
								? inboundSipDomain !== "" && // Check this only if enableInboundCalling is true
									applicationFriendlyName !== "" &&
									business !== undefined &&
									selectedMobileApp !== undefined
								: applicationFriendlyName !== "" &&
									business !== undefined &&
									selectedMobileApp !== undefined
						}
						secondaryButtonLabel={"Cancel"}
						secondaryButtonEnabled={true}
						primaryButtonAction={handleSubmitApplication}
						secondaryButtonAction={handleCancel}
					></ActionPanel>
				</div>
			</div>

			{/* showContactCenterMandatoryOption */}
			<Dialog
				open={showContactCenterMandatoryOption}
				onClose={toggleShowContactCenterMandatoryOption}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label={"Contact center is required"}>
						<div className="flex flex-col items-center justify-center gap-4">
							<span className="font-Poppins font-medium text-lg">
								For an unmanaged application, selecting a
								Contact Center is mandatory.
							</span>
							<ButtonPro
								label={"Ok"}
								onClick={() => {
									toggleShowContactCenterMandatoryOption();
									showCCList();
								}}
							></ButtonPro>
						</div>
					</Banner>
				</DialogContent>
			</Dialog>

			{/* business number selection */}
			<Dialog
				open={numberDialogOpen}
				onClose={toggleNumberDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<BusinessNumbersList
						businessNumbers={approvedBusinessNumbers}
						onAddNewClick={function (): void {
							console.log(``);
						}}
						onSelect={function (
							selectedNumbers: IBusinessNumberModel[]
						): void {
							if (selectedNumbers.length > 0) {
								// Update the approvedBusinessNumbers list
								setApprovedBusinessNumbers((prevNumbers) =>
									prevNumbers.map(
										(item) =>
											selectedNumbers.some(
												(selected) =>
													selected.businessNumber ===
													item.businessNumber
											)
												? { ...item, selected: true } // Mark as selected
												: { ...item, selected: false } // Unmark as selected
									)
								);
								// setSelectedBusinessNumbers(() =>
								// 	selectedNumbers.filter((p) => p.selected)
								// ); // Update the selected business numbers
							}
						}}
						onClose={toggleNumberDialog}
						selectMode={true}
					/>
				</DialogContent>
			</Dialog>

			<Dialog
				open={selectBusinessDialogOpen}
				onClose={toggleBusinessDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label={"Select Business"}>
						<BusinessList
							onAddNewClick={handleOnAddBusiness}
							onSelect={handleOnSelectBusiness}
							selectMode={true}
							withStatuses={["Approved", "Active"]}
						></BusinessList>
					</Banner>
				</DialogContent>
			</Dialog>

			<Dialog
				open={selectAppDialogOpen}
				onClose={toggleAppDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label={"Select Mobile App"}>
						<MobileAppList
							onAddNewClick={handleOnAddBusiness}
							onSelectClicked={handleOnSelectApp}
							selectMode={true}
						></MobileAppList>
					</Banner>
				</DialogContent>
			</Dialog>

			<Dialog
				open={selectCCDialogOpen}
				onClose={toggleCCDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label={"Select Mobile App"}>
						<ContactCentreList
							onAddNewClick={handleOnAddBusiness}
							onSelect={handleOnSelectCC}
							selectMode={true}
						></ContactCentreList>
					</Banner>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ApplicationAddNew;
